// Injection seam for the shared transaction CAPTURE interfaces — the
// multi-tabular spreadsheet view (EmbedCaptureTabularPreview), the
// list+form input view (EmbedCapturePreview) and their shared record
// state machine (useEmbedRecords).
//
// The package carries no HTTP client of its own: each host app fills an
// `EmbedDataApi` with its own transport (the platform passes its
// `embedDataApi` adapter — site-mode vs author-only routing included —
// and the standalone runtime client will pass its own in a later phase).
// Signatures mirror the platform's embedDataApi functions exactly so the
// moved call sites stay verbatim.

import type { DataAccess } from '../../dataBinding'
import type { RunActionRequest, TxnRuntime, UploadResult } from '../context'
import type { ListResponse, RunActionResponse } from '../contracts'
import type {
  BatchSaveBody, BatchSaveResult, EnforcedOverrides, ListFilter,
} from '../screen/contract'
import type { GridLayoutPersistence } from '../list/useGridLayout'

/** Which designer-defined enforced-filter set a list fetch runs under.
 *  Wire twin of the platform's `FilterSetSelection` (livepreview api). */
export type FilterSetSelection = {
  filterSetId?: string; bypass?: boolean
  /** The list TAB (`list_view.tabs`) the records are listed under — the
   *  server reads its conditions from the model. */
  tab?: string
}

// `content` is the raw transaction JSON; left loose (like the platform
// loadTransactionObject) so the capture previews unwrap `content.transaction`
// without per-call casts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EmbedTxnModelResponse = { success: boolean; content: any; message?: string }

/**
 * Backend data adapter for an embedded transaction, keyed by the shared
 * `DataAccess` (site mode → end-user endpoints, otherwise the host's
 * author/runtime endpoints). Mirrors the platform's `embedDataApi` module
 * surface as used by the capture stack.
 */
export interface EmbedDataApi {
  /** Load the transaction model (column metadata + sql_models). */
  loadModel(
    da: DataAccess | undefined, project: string, objName: string,
  ): Promise<EmbedTxnModelResponse>
  /** Fetch one page of saved records (header or a child form's rows). */
  list(
    da: DataAccess | undefined, project: string, objName: string,
    limit?: number, offset?: number, formNo?: string,
    parentPk?: Record<string, unknown>, filters?: ListFilter[],
    enforcedOverrides?: EnforcedOverrides,
    filterSet?: FilterSetSelection,
  ): Promise<ListResponse>
  /** Resolve project-variable defaults for a new record. */
  resolveDefaults(
    da: DataAccess | undefined, project: string, objName: string,
  ): Promise<{ ok?: boolean; values: Record<string, unknown> }>
  /** The generic execute funnel (item_change / row_add / row_edit / …).
   *  Same shape as the shared ItemChangeExecutor. */
  execute(
    da: DataAccess | undefined, project: string, objName: string,
    action: string, payload?: Record<string, unknown>,
  ): Promise<unknown>
  /** Atomic multi-form save: header + nested child tree in ONE transaction. */
  executeBatch(
    da: DataAccess | undefined, project: string, objName: string,
    body: BatchSaveBody,
  ): Promise<BatchSaveResult>
  /** Run a custom action button. Throws the standard error envelope on a
   *  pre-action validation failure. */
  runAction(
    da: DataAccess | undefined, project: string, objName: string,
    req: RunActionRequest,
  ): Promise<RunActionResponse>
  /** Document/image upload transport factory for the embed's forms/cells.
   *  Returns the served URL to store on the column. */
  makeUploader(
    da: DataAccess | undefined, project: string,
  ): (file: File, meta?: { docType?: string; recordId?: string; replaces?: string }) => Promise<UploadResult>
}

/**
 * Everything a capture interface needs from its host, bundled the way
 * TxnScreen takes its `TxnScreenRuntime`. The platform's designer adapter
 * (`makeDesignerCaptureRuntime`) fills it with embedDataApi + the designer
 * TxnRuntime (live-preview endpoints + toast) + designer layout persistence
 * + the live-preview deploy.
 */
export interface CaptureRuntime {
  /** Backend data adapter (model / list / execute / batch save / upload). */
  api: EmbedDataApi
  /** The shared form-stack transport + notifier. The capture components wrap
   *  their tree in <TxnRuntimeProvider value={runtime.txn}> so the core
   *  FormRenderer / InlineCellEditor / ChildTabularGrid (and their
   *  LookupModal) get a transport, exactly as the platform wrappers did. */
  txn: TxnRuntime
  /** Where "Apply layout" persists column width/order. */
  layout: GridLayoutPersistence
  /** How "Apply layout" persists (mirrors TxnScreen's `gridLayoutMode`):
   *  'design' bakes the change into the SHARED core transaction design (the
   *  designer); 'customize' writes a per-tenant model overlay (the platform Run
   *  view + the deployed runtime) so a tenant's column tweak never mutates the
   *  shared design. Absent ⇒ 'design' (keeps the designer's behavior). */
  layoutMode?: 'design' | 'customize'
  /** Prepare the backing store before the first fetch (the designer deploys
   *  the live-preview schema here, idempotently; the standalone runtime is
   *  already deployed and omits it — absent ⇒ immediately ready). */
  ensureReady?: (project: string, objName: string) => Promise<void>
}

// Apply an action's item_change-style result (or the first row a user picked
// from a select_grid) to a record by routing each value through the embed's
// existing per-field change handler (which re-stages it + fires item_change).
// Capture embeds edit one record at a time, so select_grid merges the first row.
export function applyActionResultViaOnChange(
  res: RunActionResponse, formNo: string,
  onChange: (key: string, value: unknown) => void,
): void {
  if (res.handler === 'select_grid') {
    const r = (res.rows || [])[0]
    if (r) for (const [k, v] of Object.entries(r)) onChange(k, v)
    return
  }
  const set = { ...(res.updates || {}), ...(res.forms?.[formNo]?.set || {}) }
  for (const [k, v] of Object.entries(set)) onChange(k, v)
}
